import React,{useState} from "react";
import { connect } from "react-redux";
import { makeStyles } from 'tss-react/mui';
import { TextField, Button } from "@mui/material";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import { setError } from "../store/features/error/errorSlice";
import { reserveConference } from "../utils/utils";
import AdminDialog, { DialogSwitchRow } from "./adminDialog";

const mapDispatchToProps = {
  setError,
};

const useStyles = makeStyles()((theme) => ({
  field: {
    "& .MuiInputBase-input::placeholder": {
      fontSize: "0.85em",
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  textButton: {
    textTransform: "none",
    color: theme.palette.text.secondary,
  },
  primary: {
    textTransform: "none",
    minWidth: 112,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
  },
}));

const CreateConference = (props) => {
    const { classes } = useStyles();
    const { open, setOpen, setError, defaultValues,reloadNotifications, conferences } = props;
    const [conferenceName, setConferenceName] = useState("");
    const [statusPrivate, setStatusPrivate] = useState(false);
    const [saving, setSaving] = useState(false);

    const name = conferenceName.trim();
    const canCreate = name.length > 0 && !saving;

    // One of your own conferences already has this name. Not an error: the
    // server allows it, but two rooms with one name are easy to mix up.
    const nameTaken = name !== "" && (conferences || []).some((item) =>
      String(item.owner || "").toLowerCase() === "yes" &&
      String(item.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    const handleCreateConference = async () => {
      if (!canCreate) return;
      setSaving(true);
      // A copy: defaultValues is shared with the rest of the conference view.
      const values = {
        ...defaultValues,
        conferenceData: {
          name: name,
          type: statusPrivate?"private":"public",
        },
      };
      try {
        let res = await reserveConference(values);

        if (res instanceof Error) throw res;

        setError({
          visible: true,
          type: "success",
          message: `Conference successfully created.`,
        });
        reloadNotifications();
        setOpen(false);
      } catch (error) {
        const {name,message} = error;
        setSaving(false);
        setError({
          visible: true,
          type: "error",
          // The server answers "Not found" when every room is taken.
          message:
            String(message || "").toLowerCase() === "not found"
              ? "No conference room is available to reserve right now."
              : `${name}: ${message}`,
        });
      }
    }

    if (!open) return null;

    return (
      <AdminDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={<GroupsIcon />}
        title="New conference"
        subtitle="A room people join by phone with a PIN."
        maxWidth={460}
        footer={
          <>
            <Button
              onClick={() => setOpen(false)}
              className={classes.textButton}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canCreate}
              className={classes.primary}
              onClick={handleCreateConference}
            >
              {saving ? "Creating…" : "Create"}
            </Button>
          </>
        }
      >
        <TextField
          required
          autoFocus
          id="conference-name"
          label="Conference name"
          placeholder="For example, Weekly sales call"
          value={conferenceName}
          onChange={(e) => setConferenceName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleCreateConference(); }}
          variant="outlined"
          size="small"
          fullWidth
          className={classes.field}
          helperText={nameTaken ? "You already have a conference with this name." : "Its PINs appear in the list once it is created."}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { maxLength: 60, autoComplete: "off" },
          }}
        />
        <DialogSwitchRow
          label={statusPrivate ? "Private" : "Public"}
          hint={statusPrivate
            ? "Only you see it in the list. Share the PIN to invite people."
            : "Listed for everyone in your company under Public."}
          checked={statusPrivate}
          onChange={() => setStatusPrivate(!statusPrivate)}
        />
      </AdminDialog>
    );
}

const mapStateToProps = (state) => {
  return {
    conferences: state.conferences,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateConference);
